import { type CreateSupportTicket, PriorityEnum, useApiSupportV1TicketCreate } from 'api';
import { type FieldValues, Form, SelectField, type SubmitHandler, TextField, useForm } from 'form';
import { useTranslation } from 'i18n';
import { Alert, Box, Button, Grid } from 'ui';

import { useTradeIDQueryParams } from '../../hooks/useTradeIDQueryParams/useTradeIDQueryParams';
import { getTeamRequestCategories } from './getTeamRequestCategories';
import { priorityOptions, teamOptions } from './options';
import { Success } from './Success';

const defaultValues = {
  priority: PriorityEnum.Medium,
};
const useCreateSupportTicket = () => {
  const { mutateAsync, ...rest } = useApiSupportV1TicketCreate();
  const { companyId, dealId, inspectionId, loadName, purchaseOrderSlug, salesOrderSlug, shipmentName } =
    useTradeIDQueryParams();

  const createSupportTicket = async (values: FieldValues) => {
    await mutateAsync({
      data: {
        company_id: companyId,
        deal_id: dealId,
        description: values.description,
        inspection_id: inspectionId,
        load_name: loadName,
        priority: values.priority,
        purchase_order_slug: purchaseOrderSlug,
        request_category: values.request_category,
        sales_order_slug: salesOrderSlug,
        shipment_name: shipmentName,
        team: values.team,
      },
    });
  };

  return {
    createSupportTicket,
    ...rest,
  };
};

export const SupportIssueForm = () => {
  const { t } = useTranslation('orders');
  const { createSupportTicket, data: ticket, isPending, error } = useCreateSupportTicket();

  const form = useForm<CreateSupportTicket>({
    defaultValues,
  });

  const {
    formState: { isDirty },
    reset,
    watch,
  } = form;

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    await createSupportTicket(data);
    reset(data, { keepDefaultValues: false, keepValues: true });
  };
  const selectedTeam = watch('team');
  const requestCategoryOptions = getTeamRequestCategories(selectedTeam);

  if (ticket) {
    return <Success ticket={ticket} />;
  }

  return (
    <Form {...form} onSubmit={onSubmit}>
      {error ? (
        <Box
          sx={{
            paddingY: 2,
          }}
        >
          <Alert severity={'error'}>{error.message}</Alert>
        </Box>
      ) : null}
      <Grid container spacing={3}>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <SelectField label={t('supportIssueForm.labels.team')} name="team" options={teamOptions} required />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <SelectField
            label={t('supportIssueForm.labels.requestCategory')}
            name="request_category"
            options={requestCategoryOptions}
            required
            disabled={!selectedTeam}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <SelectField
            label={t('supportIssueForm.labels.priority')}
            name="priority"
            options={priorityOptions}
            required
          />
        </Grid>
        <Grid size={12}>
          <TextField label={t('supportIssueForm.labels.description')} name="description" multiline rows={10} required />
        </Grid>
        <Grid size={12}>
          <Box
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              display: 'flex',
              paddingBlockEnd: 3,
            }}
          >
            <Button type="submit" size="large" loading={isPending} disabled={!isDirty}>
              {t('supportIssueForm.actions.createSupportIssue')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Form>
  );
};
